"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Renders the first `initial` items of a horizontal track and adds `batch`
 * more each time the track nears its end.
 *
 * The five carousels on the home page hold 59 cards between them. Mounting all
 * of them costs about a thousand DOM nodes before anyone has scrolled to the
 * section, which is what Lighthouse reports as excessive DOM size and pays for
 * again in layout and style cost on the main thread. Two screens' worth is
 * enough that the growth is never visible: by the time the track is scrolled
 * far enough to need more, the next batch is already mounted.
 *
 * The listener is rAF-throttled and passive, so it never blocks the scroll it
 * is measuring, and it detaches as soon as there is nothing left to reveal.
 */
export const useRevealOnScroll = (
  total: number,
  { initial, batch }: { initial: number; batch: number },
) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(initial, total),
  );

  const fullyRevealed = visibleCount >= total;

  const revealMore = useCallback(() => {
    setVisibleCount((count) => Math.min(total, count + batch));
  }, [total, batch]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || fullyRevealed) return;

    let frame = 0;

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        // One clientWidth of runway left — roughly a screen of warning.
        const remaining =
          track.scrollWidth - track.clientWidth - track.scrollLeft;
        if (remaining < track.clientWidth) revealMore();
      });
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [revealMore, fullyRevealed]);

  return { trackRef, visibleCount, fullyRevealed, revealMore };
};

export default useRevealOnScroll;
